class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.overlay = document.querySelector('[data-cart-overlay]');
    this.body = document.querySelector('[data-cart-body]');
    this.footer = document.querySelector('[data-cart-footer]');
    this.subtotalEl = document.querySelector('[data-cart-subtotal]');
    this.totalEl = document.querySelector('[data-cart-total]');

    if (!this.drawer) return;

    // Close buttons
    document.querySelectorAll('[data-cart-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });
    this.overlay.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      }
    });

    // Cart icon toggle — open drawer instead of navigating to /cart/
    document.querySelectorAll('[data-cart-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.showLoading();
        this.open();
        this.refreshCart();
      });
    });

    // Intercept all add-to-cart forms
    this.interceptForms();

    // Discount code handler
    this.bindDiscountForm();

    // Sync header badge to authoritative cart on every page load
    this.syncBadgeFromServer();
  }

  bindDiscountForm() {
    const apply = this.drawer.querySelector('[data-apply-discount]');
    const input = this.drawer.querySelector('[data-discount-input]');
    if (!apply || !input) return;
    const submit = () => {
      const code = (input.value || '').trim();
      if (!code) return;
      // Use Shopify's discount-link route — applies the code and redirects.
      // We send users to /cart so they see the applied code before checkout.
      const returnTo = encodeURIComponent('/cart');
      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${returnTo}`;
    };
    apply.addEventListener('click', submit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    });
  }

  async syncBadgeFromServer() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await response.json();
      this.updateHeaderCount(cart.item_count);
    } catch (err) {
      // Silently ignore — badge keeps the Liquid-rendered value
    }
  }

  interceptForms() {
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form.action || !form.action.includes('/cart/add')) return;
      e.preventDefault();
      this.addToCart(form);
    });
  }

  async addToCart(form) {
    const button = form.querySelector('[type="submit"]');
    const originalText = button?.textContent;
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding...';
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Could not add to cart');
      }

      await this.refreshCart();
      this.open();
    } catch (err) {
      alert(err.message);
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = originalText;
      }
    }
  }

  async refreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.renderCart(cart);
      this.updateHeaderCount(cart.item_count);
      window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (err) {
      console.error('Failed to fetch cart:', err);
    }
  }

  formatMoney(cents) {
    const currency = document.body.dataset.currency || 'AUD';
    const value = cents / 100;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: value % 1 === 0 ? 0 : 2
    }).format(value);
  }

  showLoading() {
    this.body.innerHTML = '<div class="cart-drawer__loading"><div class="cart-drawer__spinner"></div></div>';
    this.footer.style.display = 'none';
  }

  renderCart(cart) {
    if (cart.item_count === 0) {
      this.body.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
      this.footer.style.display = 'none';
      return;
    }

    let html = '';
    cart.items.forEach((item) => {
      const img = item.image
        ? '<img src="' + this.getSizedImageUrl(item.image, '120x') + '" alt="' + this.escapeHtml(item.title) + '" width="80" height="80">'
        : '';

      html += `
        <div class="cart-drawer__item" data-line-key="${item.key}">
          <button class="cart-drawer__item-remove" data-remove-item="${item.key}" aria-label="Remove">&times;</button>
          <div class="cart-drawer__item-image">${img}</div>
          <div class="cart-drawer__item-info">
            <p class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</p>
            <p class="cart-drawer__item-meta">${item.quantity} &times; ${this.formatMoney(item.original_price)}</p>
          </div>
          <div class="cart-drawer__item-qty">
            <button type="button" data-qty-update="${item.key}" data-qty="${item.quantity - 1}">-</button>
            <span>${item.quantity}</span>
            <button type="button" data-qty-update="${item.key}" data-qty="${item.quantity + 1}">+</button>
          </div>
        </div>
      `;
    });

    this.body.innerHTML = html;
    this.footer.style.display = '';
    if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(cart.total_price);
    this.renderDiscounts(cart);

    // Bind remove & quantity buttons
    this.body.querySelectorAll('[data-remove-item]').forEach((btn) => {
      btn.addEventListener('click', () => this.updateItem(btn.dataset.removeItem, 0));
    });
    this.body.querySelectorAll('[data-qty-update]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const qty = Math.max(0, parseInt(btn.dataset.qty, 10));
        this.updateItem(btn.dataset.qtyUpdate, qty);
      });
    });
  }

  async updateItem(key, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      await this.refreshCart();
    } catch (err) {
      console.error('Failed to update cart:', err);
    }
  }

  renderDiscounts(cart) {
    const wrap = document.querySelector('[data-cart-applied-discounts]');
    if (!wrap) return;
    const codes = (cart.cart_level_discount_applications || [])
      .concat(cart.discount_applications || [])
      .filter((d) => d.title || d.code);
    if (codes.length === 0) {
      wrap.hidden = true;
      wrap.innerHTML = '';
      return;
    }
    wrap.hidden = false;
    wrap.innerHTML = codes.map((d) => {
      const label = this.escapeHtml(d.title || d.code || '');
      const amount = d.total_allocated_amount != null ? '−' + this.formatMoney(d.total_allocated_amount) : '';
      return `<div class="cart-drawer__discount-applied"><span>${label}</span><span>${amount}</span></div>`;
    }).join('');
  }

  updateHeaderCount(count) {
    document.querySelectorAll('.mini-cart-count, [data-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.classList.toggle('is-empty', count === 0);
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  getSizedImageUrl(src, size) {
    if (!src) return '';
    return src.replace(/(\.[^.]+)$/, '_' + size + '$1');
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

window.addEventListener('DOMContentLoaded', () => {
  new CartDrawer();
});
